import { useState } from 'react'
import { Link } from 'react-router'
import { ArrowLeft, Plus, Pencil, Loader2, ChevronRight, Hash, Lightbulb } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { TagBadge } from '@/components/tag/tag-badge'
import { TagEditSheet } from '@/components/tag/tag-edit-sheet'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useQuery } from '@tanstack/react-query'
import { useTags, useDeleteTag } from '@/lib/hooks'
import type { Tag } from '@/lib/hooks'
import { fetchTagUsage } from '@/lib/api'

export default function TagsPage() {
  const { data: tags = [], isLoading } = useTags()
  const deleteTag = useDeleteTag()

  const [sheetOpen, setSheetOpen] = useState(false)
  const [sheetTarget, setSheetTarget] = useState<Tag | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Tag | null>(null)

  const { data: deleteUsage, isLoading: deleteUsageLoading } = useQuery({
    queryKey: ['tag-usage', deleteTarget?.id],
    queryFn: () => fetchTagUsage(deleteTarget!.id),
    enabled: deleteTarget !== null,
    staleTime: 0,
  })

  const openCreate = () => {
    setSheetTarget(null)
    setSheetOpen(true)
  }

  const openEdit = (tag: Tag) => {
    setSheetTarget(tag)
    setSheetOpen(true)
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    deleteTag.mutate(deleteTarget.id, {
      onSettled: () => setDeleteTarget(null),
    })
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-lg px-4 py-6">
        <div className="mb-6 flex items-center gap-2">
          <Link to="/settings" replace>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <h1 className="text-xl font-semibold">タグ</h1>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <div className="mb-4 flex items-center gap-2">
        <Link to="/settings" replace>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="size-5" />
          </Button>
        </Link>
        <h1 className="text-xl font-semibold">タグ</h1>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-lg border px-3 py-2.5">
        <Lightbulb className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="space-y-1.5">
          <p className="text-sm text-muted-foreground">
            タグを使うとカテゴリを横断して取引をグルーピングできます。
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground">例:</span>
            <TagBadge name="沖縄旅行2025夏" color="#3B82F6" />
            <TagBadge name="ふるさと納税" color="#47B881" />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {tags.map(tag => (
          <Card key={tag.id} className="py-0">
            <CardContent className="flex items-center gap-2 px-2 py-1.5">
              <Hash
                className="ml-2 size-4 shrink-0"
                style={{ color: tag.color }}
              />
              <Link
                to={`/tags/${tag.id}`}
                className="flex flex-1 items-center gap-1.5 truncate py-2 text-sm font-medium"
              >
                {tag.name}
                <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openEdit(tag)}
                aria-label={`${tag.name} を編集`}
              >
                <Pencil className="size-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
        <button
          type="button"
          onClick={openCreate}
          className="flex w-full items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-accent"
        >
          <Plus className="size-4" />
          タグを追加
        </button>
      </div>

      <TagEditSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        tag={sheetTarget}
        onDelete={setDeleteTarget}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>タグを削除しますか？</DialogTitle>
            <DialogDescription>
              {deleteUsageLoading
                ? '使用状況を確認中...'
                : !deleteUsage
                  ? '使用状況を取得できませんでした。削除すると、このタグが使用されている取引からも外れます。'
                  : deleteUsage.count > 0
                    ? `このタグは ${deleteUsage.count} 件の取引で使用されています。削除すると、すべての取引からこのタグが外れます。`
                    : 'このタグは使用されていません。'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              キャンセル
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteTag.isPending}
            >
              {deleteTag.isPending && <Loader2 className="size-4 animate-spin" />}
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
